import { ChangeDetectionStrategy, Component, computed } from '@angular/core';

import { ReadonlyDisplayBase } from './readonly-display.base';
import { READONLY_LINK_STYLES } from './link-styles';
import { ValueDisplayComponent } from './value-display.component';

/** What a link answer needs: where it points, what it says, and whether it leaves the app. */
interface DisplayLink {
  href: string;
  text: string;
  external: boolean;
}

/**
 * Read display for the answers that are ACTIONABLE as well as readable (#21239): an e-mail
 * address you can write to, a website you can open, a phone number a phone can dial.
 *
 * Which one it is comes from the field's own configuration — `type: 'email'`, or the
 * `props.type` (`url` / `tel`) the editable input already uses — so a module admin gets the
 * link by configuring the field, never by configuring the read view a second time. Anything
 * else falls through to the shared text display, which keeps ONE text rule
 * (`formatDisplayValue`) for every field family.
 *
 * Presentational: both inputs come from the row.
 */
@Component({
  selector: 'dd-link-display',
  imports: [ValueDisplayComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: READONLY_LINK_STYLES,
  template: `
    @if (link(); as link) {
      <!-- target/rel as attribute bindings, so a mailto/tel link carries neither: a _self target
           and a stray rel on a link that never opens a tab are noise in the accessibility tree. -->
      <a
        [href]="link.href"
        [attr.target]="link.external ? '_blank' : null"
        [attr.rel]="link.external ? 'noopener' : null"
        >{{ link.text }}</a
      >
    } @else {
      <dd-value-display [field]="field()" [value]="value()" />
    }
  `,
})
export class LinkDisplayComponent extends ReadonlyDisplayBase {
  /**
   * The link, or `null` for every answer that is not one — an unconfigured type, a non-string
   * value (a linked-entry envelope, a composite), or an empty answer, which has to read as the
   * row's em dash rather than as a link to nowhere.
   */
  readonly link = computed<DisplayLink | null>(() => {
    const raw = this.value();
    const text = typeof raw === 'string' ? raw.trim() : '';
    if (!text) {
      return null;
    }
    const field = this.field();
    const inputType = field.props?.type;

    if (field.type === 'email') {
      return { href: `mailto:${text}`, text, external: false };
    }
    if (inputType === 'url') {
      return { href: absoluteUrl(text), text, external: true };
    }
    if (inputType === 'tel') {
      // The reader keeps the spacing they typed; the dialler gets the bare number.
      return { href: `tel:${text.replace(/\s+/g, '')}`, text, external: false };
    }
    return null;
  });
}

/**
 * A stored url routinely has no scheme ("www.todo-connect.nl"). As an href that is RELATIVE,
 * so the link would navigate the portal to a route inside itself instead of leaving the app.
 * An existing scheme is left alone — including the ones Angular's URL sanitizer then
 * neutralises on the binding.
 */
function absoluteUrl(text: string): string {
  return /^[a-z][a-z0-9+.-]*:/i.test(text) ? text : `https://${text}`;
}
